import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { BottomSheetBody } from "@opensea/ui-kit"
import { useMemo } from "react"
import { useTranslations } from "use-intl"
import { TransactionFullScreen } from "../../../../../components/TransactionFullScreen"
import type { TransactionStatus } from "../../../../../polychain/hooks"
import { useFlowFinalTransactionStatus } from "../../../../hooks/useFlowFinalTransactionStatus"
import { CancelOrderFlowStatus_orderFragment } from "./CancelOrderFlowStatus.fragment"

type CancelOrderStatusProps = {
  orders: FragmentOf<typeof CancelOrderFlowStatus_orderFragment>[]
  status: TransactionStatus
}

export const CancelOrderStatus = ({
  orders,
  status,
}: CancelOrderStatusProps) => {
  const t = useTranslations("wallet.CancelOrderFlowStatus")

  // Use first order as proxy for all orders
  const order = readFragment(CancelOrderFlowStatus_orderFragment, orders[0])

  const imageUrls = useMemo(() => {
    if (order.side === "LISTING") {
      return orders.map(
        fragment =>
          readFragment(CancelOrderFlowStatus_orderFragment, fragment).item
            ?.imageUrl,
      )
    }
    if (
      order.type === "CRITERIA_COLLECTION" ||
      order.type === "CRITERIA_TRAIT"
    ) {
      return orders.map(
        fragment =>
          readFragment(CancelOrderFlowStatus_orderFragment, fragment).collection
            .imageUrl,
      )
    }
    return orders.map(
      fragment =>
        readFragment(CancelOrderFlowStatus_orderFragment, fragment).item
          ?.imageUrl,
    )
  }, [order, orders])

  const title = useMemo(() => {
    if (order.side === "LISTING") {
      return t("success.listingCanceled", { count: orders.length })
    }
    if (order.type === "CRITERIA_COLLECTION") {
      return t("success.collectionOfferCanceled", { count: orders.length })
    }
    if (order.type === "CRITERIA_TRAIT") {
      return t("success.traitOfferCanceled", { count: orders.length })
    }
    return t("success.offerCanceled", { count: orders.length })
  }, [order, orders, t])

  if (status === "pending") {
    return (
      <BottomSheetBody>
        <TransactionFullScreen
          body={t("pending.description")}
          images={imageUrls}
          loading
          title={t("pending.title")}
        />
      </BottomSheetBody>
    )
  }

  if (status === "error") {
    return (
      <BottomSheetBody>
        <TransactionFullScreen
          body={t("error.description")}
          images={imageUrls}
          monochrome
          title={t("error.title")}
        />
      </BottomSheetBody>
    )
  }

  return (
    <BottomSheetBody>
      <TransactionFullScreen images={imageUrls} monochrome title={title} />
    </BottomSheetBody>
  )
}

export function CancelOrderFlowStatus({
  orders,
}: Omit<CancelOrderStatusProps, "status">) {
  const status = useFlowFinalTransactionStatus()
  return <CancelOrderStatus orders={orders} status={status} />
}
